/**
 * AIChat — Contextual AI Chat Panel (Phase 9D.2)
 *
 * Collapsible right panel with context-aware AI assistant.
 * Knows the current page, customer, and account being viewed.
 * Supports suggested actions (clickable) and persistent session history.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAIChatContext } from './AIChatProvider';
import { useAIChat } from '../../api/hooks/use-ai-chat';
import type { AISuggestedAction } from '../../api/hooks/use-ai-chat';
import {
  MessageOutlined,
  CloseOutlined,
  DeleteOutlined,
  SendOutlined,
  RobotOutlined,
  RightOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import './AIChat.css';

// ─── Context Badge ───────────────────────────────────────────────────────────

const ContextBadge: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <span className="ai-chat-panel__context-badge">
    {label}: <strong>{value}</strong>
  </span>
);

// ─── Suggested Action Card ──────────────────────────────────────────────────

interface ActionCardProps {
  action: AISuggestedAction;
  onClick: (action: AISuggestedAction) => void;
}

const ActionCard: React.FC<ActionCardProps> = ({ action, onClick }) => (
  <button
    type="button"
    className="ai-chat-panel__action-card"
    onClick={() => onClick(action)}
  >
    <RightOutlined className="ai-chat-panel__action-icon" />
    <span>{action.label}</span>
  </button>
);

// ─── Main Component ──────────────────────────────────────────────────────────

export const AIChat: React.FC = () => {
  const navigate = useNavigate();
  const { chatOpen, closeChat, toggleChat, context } = useAIChatContext();
  const { messages, loading, sendMessage, clearMessages } = useAIChat();
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Listen for toggleAIChat CustomEvent from TopBar
  useEffect(() => {
    const handler = () => toggleChat();
    window.addEventListener('toggleAIChat', handler);
    return () => window.removeEventListener('toggleAIChat', handler);
  }, [toggleChat]);

  // Auto-scroll on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when panel opens
  useEffect(() => {
    if (chatOpen) {
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [chatOpen]);

  const handleSend = useCallback(() => {
    const trimmed = input.trim();
    if (!trimmed || loading) return;
    setInput('');
    sendMessage(trimmed, context);
  }, [input, loading, context, sendMessage]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  const handleActionClick = useCallback(
    (action: AISuggestedAction) => {
      if (action.path) {
        navigate(action.path);
        closeChat();
      }
    },
    [navigate, closeChat],
  );

  if (!chatOpen) return null;

  return (
    <div className="ai-chat-panel">
      {/* Header */}
      <div className="ai-chat-panel__header">
        <div className="ai-chat-panel__header-left">
          <RobotOutlined className="ai-chat-panel__header-icon" />
          <span className="ai-chat-panel__title">AI Assistant</span>
        </div>
        <div className="ai-chat-panel__header-actions">
          <button type="button" className="ai-chat-panel__header-btn" onClick={clearMessages} title="Clear chat">
            <DeleteOutlined />
          </button>
          <button type="button" className="ai-chat-panel__header-btn" onClick={closeChat} title="Close">
            <CloseOutlined />
          </button>
        </div>
      </div>

      {/* Context bar */}
      <div className="ai-chat-panel__context-bar">
        <ContextBadge label="Page" value={context.currentPage} />
        {context.currentEntityType && (
          <ContextBadge
            label={context.currentEntityType}
            value={context.currentEntityName ?? context.currentEntityId ?? ''}
          />
        )}
      </div>

      {/* Messages */}
      <div className="ai-chat-panel__messages">
        {messages.length === 0 && (
          <div className="ai-chat-panel__welcome">
            <RobotOutlined className="ai-chat-panel__welcome-icon" />
            <p className="ai-chat-panel__welcome-title">How can I help?</p>
            <p className="ai-chat-panel__welcome-subtitle">
              I&apos;m aware of what you&apos;re viewing. Ask me about this{' '}
              {context.currentEntityType ?? 'page'}, or anything else.
            </p>
            <div className="ai-chat-panel__suggestions">
              <button
                type="button"
                className="ai-chat-panel__suggestion"
                onClick={() => { setInput('Summarize this page'); }}
              >
                Summarize this page
              </button>
              <button
                type="button"
                className="ai-chat-panel__suggestion"
                onClick={() => { setInput('What actions can I take?'); }}
              >
                What actions can I take?
              </button>
              {context.currentEntityType === 'customer' && (
                <button
                  type="button"
                  className="ai-chat-panel__suggestion"
                  onClick={() => { setInput('Show risk assessment for this customer'); }}
                >
                  Show risk assessment
                </button>
              )}
              <button
                type="button"
                className="ai-chat-panel__suggestion"
                onClick={() => { setInput('Explain regulatory requirements'); }}
              >
                Explain regulatory requirements
              </button>
            </div>
          </div>
        )}

        {messages.map(msg => (
          <div key={msg.id} className={`ai-chat-panel__msg ai-chat-panel__msg--${msg.role}`}>
            <div className={`ai-chat-panel__bubble ai-chat-panel__bubble--${msg.role}`}>
              {msg.content}
            </div>
            {msg.suggestedActions && msg.suggestedActions.length > 0 && (
              <div className="ai-chat-panel__actions">
                {msg.suggestedActions.map((action, i) => (
                  <ActionCard key={i} action={action} onClick={handleActionClick} />
                ))}
              </div>
            )}
          </div>
        ))}

        {loading && (
          <div className="ai-chat-panel__msg ai-chat-panel__msg--assistant">
            <div className="ai-chat-panel__bubble ai-chat-panel__bubble--assistant ai-chat-panel__bubble--loading">
              <LoadingOutlined /> Thinking...
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="ai-chat-panel__input-area">
        <textarea
          ref={inputRef}
          className="ai-chat-panel__input"
          rows={1}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={`Ask about ${context.currentEntityType ?? 'this page'}...`}
          disabled={loading}
        />
        <button
          type="button"
          className="ai-chat-panel__send-btn"
          disabled={!input.trim() || loading}
          onClick={handleSend}
          title="Send"
        >
          <SendOutlined />
        </button>
      </div>
    </div>
  );
};

export default AIChat;
